import { useReportEffect } from "../../../hooks/useReportEffect";
import { ReportTop } from "../../../components/Dashboard/ReportLayout";
import { useEffect, useMemo, useState } from "react";
import { ReactComponent as BagFood } from "../../../assets/svg/icons/bagFood.svg";
import { FilterDateDashboard } from "../../../components/Dashboard/ViewDatas/FilterDate";
import { useFilter } from "../../../hooks/useFilter";
import {
    flattenSelection,
    selectionToNumericIds,
    selectionToSortedIdsKey,
    useFilterContextV2,
} from "../../../contexts/FilterContextV2";
import Constants from "../../../constants/Index";
import { DateModal } from "../../../components/Modal/DateFilter";

import { analyticsService } from "../../../services/analyticsService";
import { exportDailySnapshotCarbonToExcel } from "../../../utils/excel/exportDailySnapshot";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

export default function ExportCarbon() {
    const { t, i18n } = useTranslation();
    const {
        setDashboard,
        setSideBarOptionSelected,
        onBoardingDate,
        rangeDays,
        setRangeDays,
        setHideBatchFilter,
        setHideMicroAreaFilter,
        setSelectRangeDate,
        setSnapshot,
        snapshot
    } = useFilter();

    const { selectedFarms, selectedAreas } = useFilterContextV2();

    const farmsFetchKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasFetchKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    useEffect(() => {
        setHideBatchFilter(true);
        setHideMicroAreaFilter(true);
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("CARBON")
        setSelectRangeDate(2)
        document.title = Constants.TITLE_CARBON;

        const now = new Date();
        const lastDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);

        if (!rangeDays) {
            setRangeDays({
                from: lastDayOfPreviousMonth,
                to: now
            })
        }
    }, []);

    const handleExport = async (data: any) => {
        try {
            if (!rangeDays?.from || !rangeDays?.to) {
                alert(t("excelExport.selectPeriod"));
                return;
            }

            const startDate = format(new Date(rangeDays.from), 'yyyy-MM-dd');
            const endDate = format(new Date(rangeDays.to), 'yyyy-MM-dd');
            const filename = `daily-snapshot-by-carbono-${startDate}-to-${endDate}.xlsx`

            exportDailySnapshotCarbonToExcel(data, filename);
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            alert(t("excelExport.generationError"));
        }
    };

    const [mapByBatches, setMapByBatches] = useState(new Map());
    const [mapByMicroArea, setMapByMicroArea] = useState(new Map());

  useReportEffect((axios) => {
    const controller = new AbortController();

    if (rangeDays && rangeDays.from && rangeDays.to) {
      const farmsIds = selectionToNumericIds(selectedFarms);
      const areasIds = flattenSelection(selectedAreas)
        .filter((area) =>
          farmsIds.includes(
            Number(
              (area.info as { farm?: { id: number } } | undefined)?.farm
                ?.id,
            ),
          ),
        )
        .map((a) => Number(a.value));
      if (!farmsIds.length || !areasIds.length) { setSnapshot([]); return; }
      const startDate = format(new Date(rangeDays.from), 'yyyy-MM-dd');
      const endDate = format(new Date(rangeDays.to), 'yyyy-MM-dd');
      analyticsService.getDailySnapshot(farmsIds, areasIds, startDate, endDate, false, controller.signal, axios)
        .then((data) => {
          setSnapshot(data)

          const byBatchMap = new Map<string, any[]>();
          const byMicroAreaMap = new Map<string, any[]>();

          (data || []).forEach((item: any) => {
            const batchKey = item.batch_initials;
            const microAreaKey = item.micro_area_initials;

            const batchEntry = {
              date: item.snapshot_date,
              initials: `${item.farm_initials}-${item.area_initials}-${item.batch_initials}`,
              days: item.supplement_offer_days,
              total_category: item.total_from_category,
              total_methane_emission_kg_day: item.total_methane_emission_kg_day,
              co2_equivalent_kg: item.co2_equivalent_kg,
            };

            const microAreaEntry = {
              date: item.snapshot_date,
              initials: `${item.farm_initials}-${item.area_initials}-${item.micro_area_initials}`,
              days: item.supplement_offer_days,
              acreage: item.micro_area_acreage,
              area_ms_intake_kg_ha: item.area_ms_intake_kg_ha,
              total_methane_emission_kg_day: item.total_methane_emission_kg_day,
              co2_equivalent_kg: item.co2_equivalent_kg,
            };

            if (batchKey) {
              const batchArr = byBatchMap.get(batchKey);
              if (batchArr) batchArr.push(batchEntry);
              else byBatchMap.set(batchKey, [batchEntry]);
            }

            if (microAreaKey) {
              const microArr = byMicroAreaMap.get(microAreaKey);
              if (microArr) microArr.push(microAreaEntry);
              else byMicroAreaMap.set(microAreaKey, [microAreaEntry]);
            }
          });

          setMapByBatches(byBatchMap);
          setMapByMicroArea(byMicroAreaMap);
        })
        .catch((err) => {
          if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
            // requisição abortada, ignorar
            return;
          }
          // outros erros podem ser tratados aqui
          console.error(err);
        });
    }

    return () => {
      controller.abort();
    };

  }, [farmsFetchKey, areasFetchKey, rangeDays]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form className="dashboardPastureManagement">
                <div className="cattleMap_page">
            <ReportTop kind="carbon" icon={<BagFood height={72} />} title={t("carbon.report")} subtitle={t("carbon.data")}>
              <div>
                <FilterDateDashboard exportPDF={true} onExportExcel={() => handleExport(snapshot)} />
              </div>
            </ReportTop>

                    <div className="splitContent" style={{
                        flexDirection: "column",
                        gap: 50,
                        display: (snapshot && snapshot.length > 0) ? "flex" : "none"
                    }}>
                        <h2 style={{
                            fontFamily: "Fira Sans",
                            fontStyle: `normal`,
                            fontWeight: `600`,
                            fontSize: `22px`,
                            lineHeight: `1.375rem`,
                            textTransform: `uppercase`,
                        }}>{t("carbon.consolidatedPeriod")}</h2>
                        <div className="tableDashboard">
                            <div style={{ overflowX: "auto", width: "100%" }}>
                                <h2>{t("carbon.consolidatedBatch")}</h2>
                                {(() => {
                                    const batches = Array.from(mapByBatches.values());
                                    const microAreas = Array.from(mapByMicroArea.values());

                                    const fmt = (value: number) =>
                                        Number.isFinite(value) ? value.toLocaleString(i18n.language.startsWith("es") ? "es" : "pt-BR", { maximumFractionDigits: 2 }) : "0";

                                    return (
                                        <>
                                            <table style={{ minWidth: "max-content", width: "100%" }}>
                                                <thead style={{ color: "#fff" }}>
                                                    <tr>
                                                        <th style={{ background: "#324440", minWidth: "80px", textAlign: "left" }}>{t("carbon.batch")}</th>
                                                        <th style={{ background: "#808080", minWidth: "80px" }}>{t("carbon.days")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.animals")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.totalMethane")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.methaneAnimal")}</th>
                                                        <th style={{ background: "#324440", minWidth: "180px" }}>{t("carbon.totalCo2")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.co2Animal")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {batches
                                                        .sort((a, b) => {
                                                            const initialsA = a[0]?.initials ?? "";
                                                            const initialsB = b[0]?.initials ?? "";
                                                            return initialsA.localeCompare(initialsB);
                                                        })
                                                        .map((row: any[], index: number) => {
                                                            if (!row || row.length === 0) return null;
                                                            const totalMethane = Number(row[row.length - 1]?.total_methane_emission_kg_day ?? 0);
                                                            const totalCo2 = Number(row[row.length - 1]?.co2_equivalent_kg ?? 0);
                                                            const animals = Number(row[row.length - 1]?.total_category ?? 0);
                                                            const methanePerAnimal = animals > 0 ? totalMethane / animals : 0;
                                                            const co2PerAnimal = animals > 0 ? totalCo2 / animals : 0;

                                                            return (
                                                                <tr className={index % 2 === 0 ? "bg_white" : "bg_gray"} key={`consolidated-${index}`}>
                                                                    <td>{row[row.length - 1]?.initials ?? "—"}</td>
                                                                    <td>{row[row.length - 1]?.days ?? "0"}</td>
                                                                    <td>{animals || "0"}</td>
                                                                    <td>{fmt(totalMethane)}</td>
                                                                    <td>{fmt(methanePerAnimal)}</td>
                                                                    <td>{fmt(totalCo2)}</td>
                                                                    <td>{fmt(co2PerAnimal)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>

                                            <br />

                                            <h2>{t("carbon.consolidatedPaddock")}</h2>
                                            <table style={{ minWidth: "max-content", width: "100%" }}>
                                                <thead style={{ color: "#fff" }}>
                                                    <tr>
                                                        <th style={{ background: "#324440", minWidth: "80px", textAlign: "left" }}>{t("carbon.paddock")}</th>
                                                        <th style={{ background: "#808080", minWidth: "80px" }}>{t("carbon.days")}</th>
                                                        <th style={{ background: "#324440", minWidth: "80px" }}>{t("carbon.area")}</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>{t("carbon.consumedForage")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.totalMethane")}</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>{t("carbon.methaneArea")}</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>{t("carbon.co2Equivalent")}</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>{t("carbon.co2Area")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {microAreas
                                                        .sort((a, b) => {
                                                            const initialsA = a[0]?.initials ?? "";
                                                            const initialsB = b[0]?.initials ?? "";
                                                            return initialsA.localeCompare(initialsB);
                                                        })
                                                        .map((row: any[], index: number) => {
                                                            if (!row || row.length === 0) return null;
                                                            const totalForage = Number(row[row.length - 1]?.area_ms_intake_kg_ha ?? 0);
                                                            const totalMethane = Number(row[row.length - 1]?.total_methane_emission_kg_day ?? 0);
                                                            const totalCo2 = Number(row[row.length - 1]?.co2_equivalent_kg ?? 0);
                                                            const acreage = Number(row[row.length - 1]?.acreage ?? 0);
                                                            const methanePerAcre = acreage > 0 ? totalMethane / acreage : 0;
                                                            const co2PerAcre = acreage > 0 ? totalCo2 / acreage : 0;

                                                            return (
                                                                <tr className={index % 2 === 0 ? "bg_white" : "bg_gray"} key={`pasto-${index}`}>
                                                                    <td>{row[row.length - 1]?.initials ?? "—"}</td>
                                                                    <td>{row[row.length - 1]?.days ?? "0"}</td>
                                                                    <td>{acreage || "0"}</td>
                                                                    <td>{fmt(totalForage)}</td>
                                                                    <td>{fmt(totalMethane)}</td>
                                                                    <td>{fmt(methanePerAcre)}</td>
                                                                    <td>{fmt(totalCo2)}</td>
                                                                    <td>{fmt(co2PerAcre)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}
